import React from "react";
import {TFunction} from "i18next";
import {useTranslation} from "react-i18next";
import Table, { TableProps } from "@awsui/components-react/table";
import Competition from "model/Competition";
import StatsItem from "model/StatsItem";
import getStats from "api/getStats";
import { useQuery } from "react-query";
import TextFilter from "@awsui/components-react/text-filter";
import CollectionPreferences from "@awsui/components-react/collection-preferences";
import useTable from "hooks/useTable";
import Pagination from "@awsui/components-react/pagination";
import CompetitionsHeader from "components/CompetitionsHeader";
import {useAuth} from "hooks/useAuth";
import Rider from "model/Rider";
import FlagIcon from "components/icons/FlagIcon";
import {formatNumber} from "common/formatNumber";
import RiderPopover from "components/RiderPopover";


interface StatsTableProps {
    competition: Competition;
}

const buildColumnDefinitions = (t: TFunction, tCommon: TFunction): TableProps.ColumnDefinition<StatsItem>[] => [
    {
        id: "name",
        header: t("columns.name"),
        cell: (item: StatsItem) => <RiderPopover rider={item} />,
        sortingField: "name"
    },
    {
        id: "team",
        header: t("columns.team"),
        cell: (item: StatsItem) => item.team,
        sortingField: "team"
    },
    {
        id: "country",
        header: t("columns.country"),
        cell: (item: Rider) => item.country && <FlagIcon country={item.country}/>,
        sortingField: "country"
    },
    {
        id: "points",
        header: t("columns.points"),
        cell: (item: StatsItem) => item.points,
        sortingField: "points"
    },
    {
        id: "value",
        header: t("columns.value"),
        cell: (item: StatsItem) => `${item.value} ${tCommon("common.million")}`,
        sortingField: "value"
    },
    {
        id: "efficiency",
        header: t("columns.efficiency"),
        cell: (item: StatsItem) => formatNumber(item.efficiency, 2),
        sortingField: "efficiency"
    },
    {
        id: "ideal",
        header: t("columns.ideal"),
        cell: (item: StatsItem) => item.ideal ? tCommon("yes") : tCommon("no"),
        sortingField: "ideal"
    },
    {
        id: "pickedPercentage",
        header: t("columns.pickedPercentage"),
        cell: (item: StatsItem) => item.pickedPercentage ? `${formatNumber(item.pickedPercentage, 2)} %` : "-",
        sortingField: "pickedPercentage"
    },
];

const StatsTable: React.FC<StatsTableProps> = (props) => {

    const {t} = useTranslation("stats");
    const {t: tCommon} = useTranslation("common");
    const competition = props.competition;

    const [{user}] = useAuth();

    const {
        isLoading: statsIsLoading,
        data: stats,
        error: getStatsError
    } = useQuery<StatsItem[], Error>(`getStats-${competition.code}`, () => getStats(competition, user?.token));

    const {
        preferences,
        setPreferences,
        items,
        collectionProps,
        filterProps,
        paginationProps,
        collectionPreferencesProps
    } = useTable(stats || [], {
        emptyStateSubtitle: getStatsError as Error,
        emptyStateTitle: t("empty"),
        pageSize: 50
    });


    return (
        <Table
            {...collectionProps}
            loadingText={t("loading")}
            columnDefinitions={buildColumnDefinitions(t, tCommon)}
            items={items}
            loading={statsIsLoading}
            wrapLines={false}
            header={<CompetitionsHeader competition={competition} />}
            pagination={
                <Pagination
                    {...paginationProps}
                    ariaLabels={{
                        nextPageLabel: tCommon("table.nextPage"),
                        previousPageLabel: tCommon("table.previousPage"),
                        pageLabel: pageNumber => tCommon("table.page", {page: pageNumber})
                    }}
                />
            }
            filter={
                <TextFilter
                    {...filterProps}
                    filteringPlaceholder={tCommon("table.filterPlaceholder")}
                />
            }
            preferences={
                <CollectionPreferences
                    {...collectionPreferencesProps}
                    preferences={preferences}
                    onConfirm={({ detail }) => setPreferences(detail)}
                />
            }
        />
    );
};

export default StatsTable;
